import { type FC } from 'react';

import clsx from 'clsx';

import { Flex, type FlexProps } from '@/components/common/Flex';
import { OverflowingTextTip } from '@/components/common/OverflowingTextTip';
import { type WithClassName } from '@/types/react';
import { type PlainColor } from '@/types/theme';
import { getColorCssValue, getColorCssValueWithAlpha, isHexColor, isNativeColor } from '@/utils/theme';

import styles from './Legend.module.scss';

export type LegendVariant = 'default' | 'borderOnly' | 'dashedBorderOnly';

export type Dataset = {
  id: string;
  name: string;
  value?: string;
  color: PlainColor;
  variant?: LegendVariant;
};

export type LegendProps = WithClassName & {
  datasets: Dataset[];
  direction?: FlexProps['direction'];
  alignItems?: FlexProps['alignItems'];
  wrap?: FlexProps['wrap'];
  gap?: number;
  variant?: LegendVariant;
};

export const Legend: FC<LegendProps> = (props) => {
  const { className, datasets, direction, alignItems, wrap = 'wrap', gap = 3, variant = 'default' } = props;

  if (!datasets.length) {
    return null;
  }

  return (
    <Flex
      className={clsx(styles.root, direction === 'column' && styles.rootColumn, className)}
      direction={direction}
      gap={gap}
      wrap={wrap}
      childMinSize="unset"
      justifyContent="center"
      alignItems={alignItems ?? (direction === 'column' ? 'stretch' : 'center')}
    >
      {datasets.map(({ id, name, value, color, variant: datasetVariant }) => {
        const itemVariant = datasetVariant ?? variant;
        const isGradientCompatibleColor = isHexColor(color) || !isNativeColor(color);

        const style =
          itemVariant === 'borderOnly' || itemVariant === 'dashedBorderOnly'
            ? {
                background: 'transparent',
                borderColor: getColorCssValue(color),
                borderImageSource: undefined,
              }
            : {
                background: isGradientCompatibleColor
                  ? `linear-gradient(180deg, ${getColorCssValueWithAlpha(color, 0.26)} 0%, ${getColorCssValueWithAlpha(
                      color,
                      0
                    )} 100%)`
                  : getColorCssValue(color),
                borderColor: isGradientCompatibleColor ? undefined : getColorCssValue(color),
                borderImageSource: isGradientCompatibleColor
                  ? `linear-gradient(180deg, ${getColorCssValue(color)} 0%, ${getColorCssValueWithAlpha(
                      color,
                      0.36
                    )} 100%)`
                  : undefined,
              };

        return (
          <Flex
            key={id}
            className={styles.label}
            gap={3}
            alignItems={value && direction === 'column' ? 'flexStart' : 'center'}
          >
            <div
              className={clsx(styles.square, itemVariant === 'dashedBorderOnly' && styles.squareDashedBorderOnly)}
              style={style}
            />

            {value ? (
              <Flex
                className={styles.caption}
                direction={direction === 'column' ? 'column' : 'row'}
                gap={direction === 'column' ? 1 : 3}
                alignItems={direction === 'column' ? 'stretch' : 'center'}
                childMinSize="unset"
              >
                <OverflowingTextTip className={styles.name} type="regular14" lineCount={1}>
                  {name}
                </OverflowingTextTip>

                <OverflowingTextTip className={styles.value} type="regular14" lineCount={1}>
                  {value}
                </OverflowingTextTip>
              </Flex>
            ) : (
              <OverflowingTextTip className={styles.name} type="regular14" lineCount={1}>
                {name}
              </OverflowingTextTip>
            )}
          </Flex>
        );
      })}
    </Flex>
  );
};
